import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Toggle from "@/components/base/Toggle";
import AdminLayout from "@/components/layout/AdminLayout";
import {
  getPlatformDgSyncConfig,
  isDgSyncInProgress,
  savePlatformDgSyncConfig,
  type DgSyncConfig,
  type DgSyncSaveRequest,
  type DgSyncStatus,
} from "@/services/dgSync";

// ─── Types ───
interface SyncStats {
  lastSyncTime: string;
  lastSyncStatus: DgSyncStatus | "";
  syncedUserCount: number;
  failedReason?: string;
}

interface SectionConfig {
  dgEmail: string;
  includeNested: boolean;
  status: "Enabled" | "Disabled";
  description: string;
  lastSavedAt: string | null;
  syncStats: SyncStats;
}

interface SectionHandle {
  validateAndGet: () => DgSyncSaveRequest | null;
}

// 同步进行中时的轮询间隔与最长轮询时间。
const POLL_INTERVAL_MS = 3000;

// 把后端返回的同步状态映射为 section 展示所需的统计信息。
function toSyncStats(item: DgSyncConfig): SyncStats {
  return {
    lastSyncTime: item.lastSyncCompletedAt ?? "",
    lastSyncStatus: item.lastSyncStatus ?? "",
    syncedUserCount: item.syncedUserCount ?? 0,
    failedReason: item.lastSyncError ?? undefined,
  };
}

function toSectionConfig(item: DgSyncConfig | null): SectionConfig | null {
  if (!item || !item.exists) return null;
  return {
    dgEmail: item.groupEmail ?? "",
    includeNested: item.includeNested,
    status: item.isEnabled ? "Enabled" : "Disabled",
    description: item.description ?? "",
    lastSavedAt: item.lastSavedAt,
    syncStats: toSyncStats(item),
  };
}

function toSaveRequest(item: DgSyncConfig, isEnabled: boolean): DgSyncSaveRequest {
  return {
    groupEmail: item.groupEmail ?? "",
    includeNested: item.includeNested,
    isEnabled,
    description: item.description,
  };
}

// ─── Reusable UI Components ───
function Row({
  label,
  value,
  tooltip,
}: {
  label: string;
  value: React.ReactNode;
  tooltip?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-72 flex-shrink-0 flex items-center gap-1.5">
        <span className="text-sm font-semibold text-gray-800">{label}</span>
        {tooltip}
      </div>
      <div className="flex-1 text-sm text-gray-700">{value}</div>
    </div>
  );
}

function StatusText({ enabled, t }: { enabled: boolean; t: (k: string) => string }) {
  return (
    <span
      className="text-sm font-medium whitespace-nowrap"
      style={{ color: enabled ? "#830051" : "#dc2626" }}
    >
      {enabled ? t("syncConfig.enabled") : t("syncConfig.disabled")}
    </span>
  );
}

// 上次同步状态直接显示状态值，尚未同步时显示 Never。
function SyncStatusBadge({ stats }: { stats: SyncStats }) {
  if (!stats.lastSyncStatus) return <span>Never</span>;
  if (stats.lastSyncStatus === "Pending" || stats.lastSyncStatus === "Syncing") {
    return (
      <span
        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
        style={{ backgroundColor: "rgba(131,0,81,0.08)", color: "#830051" }}
      >
        <i className="ri-loader-2-line animate-spin text-xs" style={{ color: "#830051" }}></i>
        {stats.lastSyncStatus}
      </span>
    );
  }
  if (stats.lastSyncStatus === "Failed") {
    return (
      <div className="inline-flex items-center gap-1.5">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
          {stats.lastSyncStatus}
        </span>
        {stats.failedReason && (
          <QuestionTooltip>
            <span className="whitespace-pre-wrap break-words">{stats.failedReason}</span>
          </QuestionTooltip>
        )}
      </div>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
      {stats.lastSyncStatus}
    </span>
  );
}

function QuestionTooltip({ children }: { children: React.ReactNode }) {
  const [show, setShow] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <span className="w-3.5 h-3.5 flex items-center justify-center cursor-help shrink-0">
        <i className="ri-question-line text-sm" style={{ color: "#830051" }}></i>
      </span>
      {show && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-64 z-50 px-3.5 py-2.5 bg-white text-gray-700 text-[11px] rounded-lg leading-relaxed border border-gray-200"
          style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.12)" }}
        >
          {children}
          <span className="absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0 border-l-[5px] border-r-[5px] border-b-[5px] border-l-transparent border-r-transparent border-b-white" />
          <span
            className="absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent"
            style={{ borderBottomColor: "#e5e7eb", marginBottom: "1px" }}
          />
        </div>
      )}
    </div>
  );
}

function SectionHeader({
  title,
  enDesc,
  extraDesc,
}: {
  title: string;
  enDesc: string;
  extraDesc?: string;
}) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <QuestionTooltip>
          <p className="text-xs leading-relaxed">{enDesc}</p>
          {extraDesc && <p className="text-xs leading-relaxed mt-2 text-amber-600">{extraDesc}</p>}
        </QuestionTooltip>
      </div>
    </div>
  );
}

function RulesTable({ t }: { t: (k: string) => string }) {
  return (
    <div className="mt-6">
      <h4 className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 mb-4">
        {t("syncConfig.rulesTitle")}
        <QuestionTooltip>
          <p className="text-xs leading-relaxed">{t("syncConfig.rulesDesc")}</p>
        </QuestionTooltip>
      </h4>
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">
                {t("syncConfig.rulesColClass")}
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">
                {t("syncConfig.rulesColRegion")}
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">
                {t("syncConfig.rulesColRole")}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100">
              <td className="px-4 py-3 text-gray-700">{t("syncConfig.rulesRow1")}</td>
              <td className="px-4 py-3 text-gray-700">{t("syncConfig.regionHk")}</td>
              <td className="px-4 py-3 text-gray-700">{t("syncConfig.roleHkStaff")}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="px-4 py-3 text-gray-700">{t("syncConfig.rulesRow2")}</td>
              <td className="px-4 py-3 text-gray-700">{t("syncConfig.regionMainland")}</td>
              <td className="px-4 py-3 text-gray-700">{t("syncConfig.roleStaff")}</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-gray-700">{t("syncConfig.rulesRow3")}</td>
              <td className="px-4 py-3 text-gray-700">{t("syncConfig.regionHk")}</td>
              <td className="px-4 py-3 text-gray-700">{t("syncConfig.roleHkStaff")}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// 已保存配置的只读统计行（两个 section 共用）。
function SyncStatsRows({ stats, t }: { stats: SyncStats; t: (k: string) => string }) {
  return (
    <>
      <Row label={t("syncConfig.lastSyncTime")} value={stats.lastSyncTime || "—"} />
      <Row
        label={t("syncConfig.lastSyncStatus")}
        value={
          <div className="flex flex-wrap items-center gap-3">
            <SyncStatusBadge stats={stats} />
          </div>
        }
      />
      <Row label={t("syncConfig.syncedUserCount")} value={stats.syncedUserCount} />
    </>
  );
}

// ─── Section 1: All User Distribution Group Sync ───
const UserSourceSection = forwardRef<
  SectionHandle,
  {
    config: SectionConfig | null;
    isEditing: boolean;
    busy: boolean;
    onToggleStatus: (v: boolean) => void;
  }
>(function UserSourceSection({ config, isEditing, busy, onToggleStatus }, ref) {
  const { t } = useTranslation();
  const [confirmDisable, setConfirmDisable] = useState(false);

  const [dgEmail, setDgEmail] = useState("");
  const [includeNested, setIncludeNested] = useState(true);
  const [status, setStatus] = useState<"Enabled" | "Disabled">("Enabled");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (config && !isEditing) {
      setDgEmail(config.dgEmail);
      setIncludeNested(config.includeNested);
      setStatus(config.status);
      setDescription(config.description);
    }
  }, [config, isEditing]);

  const validate = () => {
    const next: Record<string, string> = {};
    if (!dgEmail.trim()) {
      next.dgEmail = t("syncConfig.s1DgEmailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dgEmail.trim())) {
      next.dgEmail = t("syncConfig.emailInvalid");
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  useImperativeHandle(ref, () => ({
    validateAndGet: () => {
      if (!validate()) return null;
      return {
        groupEmail: dgEmail.trim(),
        includeNested,
        isEnabled: status === "Enabled",
        description: description.trim() || null,
      };
    },
  }));

  const confirmDisableToggle = () => {
    if (!config || busy) return;
    setConfirmDisable(false);
    onToggleStatus(config.status !== "Enabled");
  };

  const stats = config?.syncStats;
  const enabled = config?.status === "Enabled";

  return (
    <div className="bg-white">
      <div className="px-1 pt-1 pb-8">
        <SectionHeader title={t("syncConfig.s1Title")} enDesc={t("syncConfig.s1Desc")} />

        {!isEditing && config ? (
          <div className="flex flex-col gap-4">
            <Row label={t("syncConfig.s1DgEmailLabel")} value={config.dgEmail} />
            <Row
              label={t("syncConfig.includeNested")}
              value={config.includeNested ? t("syncConfig.yes") : t("syncConfig.no")}
            />
            <Row
              label={t("syncConfig.s1StatusLabel")}
              value={
                <div className="flex items-center gap-3">
                  <Toggle
                    value={enabled}
                    disabled={busy}
                    onChange={() => setConfirmDisable(true)}
                  />
                  <StatusText enabled={enabled} t={t} />
                </div>
              }
            />
            {config.description && (
              <Row label={t("syncConfig.descriptionLabel")} value={config.description} />
            )}

            {stats && <SyncStatsRows stats={stats} t={t} />}
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {/* DG Email */}
            <div className="flex items-start gap-4">
              <div className="flex items-center gap-2 w-72 flex-shrink-0 pt-2">
                <label className="text-sm font-semibold text-gray-800">
                  {t("syncConfig.s1DgEmailLabel")}
                  <span className="text-red-400 ml-0.5">*</span>
                </label>
                <QuestionTooltip>{t("syncConfig.s1DgEmailTooltip")}</QuestionTooltip>
              </div>
              <div className="flex-1 flex flex-col gap-1">
                <input
                  type="email"
                  value={dgEmail}
                  onChange={(e) => {
                    setDgEmail(e.target.value);
                    if (errors.dgEmail) setErrors((prev) => ({ ...prev, dgEmail: "" }));
                  }}
                  placeholder={t("syncConfig.s1DgEmailPlaceholder")}
                  className={`w-full text-sm border rounded-lg px-3.5 py-2.5 outline-none focus:border-gray-400 transition-colors ${
                    errors.dgEmail ? "border-red-300 bg-red-50" : "border-gray-200"
                  }`}
                />
                {errors.dgEmail && <p className="text-[11px] text-red-500">{errors.dgEmail}</p>}
              </div>
            </div>

            {/* Include Nested */}
            <div className="flex items-start gap-4">
              <div className="w-72 flex-shrink-0" />
              <div className="flex-1 flex items-center gap-2">
                <button
                  onClick={() => setIncludeNested(!includeNested)}
                  className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors cursor-pointer ${
                    includeNested ? "border-transparent" : "border-gray-300"
                  }`}
                  style={includeNested ? { backgroundColor: "#830051" } : {}}
                >
                  {includeNested && <i className="ri-check-line text-[10px] text-white"></i>}
                </button>
                <span className="text-xs text-gray-700">{t("syncConfig.includeNested")}</span>
                <QuestionTooltip>{t("syncConfig.nestedTooltip")}</QuestionTooltip>
              </div>
            </div>

            {/* Status */}
            <div className="flex items-start gap-4">
              <div className="flex items-center gap-2 w-72 flex-shrink-0 pt-1">
                <label className="text-sm font-semibold text-gray-800">
                  {t("syncConfig.s1StatusLabel")}
                </label>
              </div>
              <div className="flex-1 flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <Toggle
                    value={status === "Enabled"}
                    onChange={(v) => setStatus(v ? "Enabled" : "Disabled")}
                  />
                  <StatusText enabled={status === "Enabled"} t={t} />
                </div>
                <p className="text-[11px] text-gray-400">
                  {status === "Enabled"
                    ? t("syncConfig.s1StatusEnabledHint")
                    : t("syncConfig.s1StatusDisabledHint")}
                </p>
              </div>
            </div>

            {/* Description */}
            <div className="flex items-start gap-4">
              <div className="flex items-center gap-2 w-72 flex-shrink-0 pt-2">
                <label className="text-sm font-semibold text-gray-800">
                  {t("syncConfig.descriptionLabel")}
                </label>
              </div>
              <div className="flex-1">
                <textarea
                  placeholder={t("syncConfig.descriptionPlaceholder")}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  maxLength={500}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3.5 py-2.5 outline-none focus:border-gray-400 resize-none transition-colors"
                />
                <p className="text-[10px] text-gray-300 mt-1 text-right">
                  {description.length}/500
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {confirmDisable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setConfirmDisable(false)} />
          <div
            className="relative bg-white rounded-xl p-6 w-full max-w-sm mx-4"
            style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.16)" }}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                <i className="ri-alert-line text-red-500" style={{ fontSize: "18px" }}></i>
              </span>
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  {enabled ? t("syncConfig.disableSyncTitle") : t("syncConfig.enableSyncTitle")}
                </h3>
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              {enabled ? t("syncConfig.disableSyncDesc") : t("syncConfig.enableSyncDesc")}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmDisable(false)}
                className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-600 cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
              >
                {t("syncConfig.cancel")}
              </button>
              <button
                onClick={confirmDisableToggle}
                className="text-sm px-5 py-2 rounded-lg text-white cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
                style={{ backgroundColor: enabled ? "#dc2626" : "#16a34a" }}
              >
                {enabled ? t("syncConfig.disableSync") : t("syncConfig.enableSync")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

// ─── Section 2: Region Assignment ───
const RegionSection = forwardRef<
  SectionHandle,
  {
    config: SectionConfig | null;
    isEditing: boolean;
    step1Done: boolean;
    busy: boolean;
    onToggleStatus: (v: boolean) => void;
  }
>(function RegionSection({ config, isEditing, step1Done, busy, onToggleStatus }, ref) {
  const { t } = useTranslation();
  const [confirmDisable, setConfirmDisable] = useState(false);

  const [hkDgEmail, setHkDgEmail] = useState("");
  const [includeNested, setIncludeNested] = useState(true);
  const [status, setStatus] = useState<"Enabled" | "Disabled">("Enabled");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (config && !isEditing) {
      setHkDgEmail(config.dgEmail);
      setIncludeNested(config.includeNested);
      setStatus(config.status);
      setDescription(config.description);
    }
  }, [config, isEditing]);

  const validate = () => {
    const next: Record<string, string> = {};
    if (!hkDgEmail.trim()) {
      next.hkDgEmail = t("syncConfig.s2HkEmailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(hkDgEmail.trim())) {
      next.hkDgEmail = t("syncConfig.emailInvalid");
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  useImperativeHandle(ref, () => ({
    validateAndGet: () => {
      if (!validate()) return null;
      return {
        groupEmail: hkDgEmail.trim(),
        includeNested,
        isEnabled: status === "Enabled",
        description: description.trim() || null,
      };
    },
  }));

  const confirmDisableToggle = () => {
    if (!config || busy) return;
    setConfirmDisable(false);
    onToggleStatus(config.status !== "Enabled");
  };

  const stats = config?.syncStats;
  const enabled = config?.status === "Enabled";

  return (
    <div className="border-t border-gray-200 pt-8">
      <div className="px-1 pt-1 pb-1">
        <SectionHeader
          title={t("syncConfig.s2Title")}
          enDesc={t("syncConfig.s2Desc")}
          extraDesc={!step1Done ? t("syncConfig.pendingBanner") : undefined}
        />

        {!isEditing && config ? (
          <div className="flex flex-col gap-4">
            <Row label={t("syncConfig.s2HkEmailLabel")} value={config.dgEmail} />
            <Row
              label={t("syncConfig.includeNested")}
              value={config.includeNested ? t("syncConfig.yes") : t("syncConfig.no")}
            />
            <Row
              label={t("syncConfig.statusLabel")}
              value={
                <div className="flex items-center gap-3">
                  <Toggle
                    value={enabled}
                    disabled={busy}
                    onChange={() => setConfirmDisable(true)}
                  />
                  <StatusText enabled={enabled} t={t} />
                </div>
              }
            />
            {config.description && (
              <Row label={t("syncConfig.descriptionLabel")} value={config.description} />
            )}

            <Row label={t("syncConfig.lastSaved")} value={config.lastSavedAt || "—"} />
            {stats && <SyncStatsRows stats={stats} t={t} />}

            <RulesTable t={t} />
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {/* HK DG Email */}
            <div className="flex items-start gap-4">
              <div className="flex items-center gap-2 w-72 flex-shrink-0 pt-2">
                <label className="text-sm font-semibold text-gray-800">
                  {t("syncConfig.s2HkEmailLabel")}
                  <span className="text-red-400 ml-0.5">*</span>
                </label>
              </div>
              <div className="flex-1 flex flex-col gap-1">
                <input
                  type="email"
                  value={hkDgEmail}
                  onChange={(e) => {
                    setHkDgEmail(e.target.value);
                    if (errors.hkDgEmail) setErrors((prev) => ({ ...prev, hkDgEmail: "" }));
                  }}
                  placeholder={t("syncConfig.s2HkEmailPlaceholder")}
                  className={`w-full text-sm border rounded-lg px-3.5 py-2.5 outline-none focus:border-gray-400 transition-colors ${
                    errors.hkDgEmail ? "border-red-300 bg-red-50" : "border-gray-200"
                  }`}
                />
                {errors.hkDgEmail && <p className="text-[11px] text-red-500">{errors.hkDgEmail}</p>}
              </div>
            </div>

            {/* Include Nested */}
            <div className="flex items-start gap-4">
              <div className="w-72 flex-shrink-0" />
              <div className="flex-1 flex items-center gap-2">
                <button
                  onClick={() => setIncludeNested(!includeNested)}
                  className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors cursor-pointer ${
                    includeNested ? "border-transparent" : "border-gray-300"
                  }`}
                  style={includeNested ? { backgroundColor: "#830051" } : {}}
                >
                  {includeNested && <i className="ri-check-line text-[10px] text-white"></i>}
                </button>
                <span className="text-xs text-gray-700">{t("syncConfig.includeNested")}</span>
                <QuestionTooltip>{t("syncConfig.nestedTooltip")}</QuestionTooltip>
              </div>
            </div>

            {/* Status */}
            <div className="flex items-start gap-4">
              <div className="flex items-center gap-2 w-72 flex-shrink-0 pt-1">
                <label className="text-sm font-semibold text-gray-800">
                  {t("syncConfig.statusLabel")}
                </label>
              </div>
              <div className="flex-1 flex items-center gap-3">
                <Toggle
                  value={status === "Enabled"}
                  onChange={(v) => setStatus(v ? "Enabled" : "Disabled")}
                />
                <StatusText enabled={status === "Enabled"} t={t} />
              </div>
            </div>

            {/* Description */}
            <div className="flex items-start gap-4">
              <div className="flex items-center gap-2 w-72 flex-shrink-0 pt-2">
                <label className="text-sm font-semibold text-gray-800">
                  {t("syncConfig.descriptionLabel")}
                </label>
              </div>
              <div className="flex-1">
                <textarea
                  placeholder={t("syncConfig.descriptionPlaceholder")}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  maxLength={500}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3.5 py-2.5 outline-none focus:border-gray-400 resize-none transition-colors"
                />
                <p className="text-[10px] text-gray-300 mt-1 text-right">
                  {description.length}/500
                </p>
              </div>
            </div>

            <RulesTable t={t} />
          </div>
        )}
      </div>

      {confirmDisable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setConfirmDisable(false)} />
          <div
            className="relative bg-white rounded-xl p-6 w-full max-w-sm mx-4"
            style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.16)" }}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                <i className="ri-alert-line text-red-500" style={{ fontSize: "18px" }}></i>
              </span>
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  {enabled ? t("syncConfig.disableRegionTitle") : t("syncConfig.enableRegionTitle")}
                </h3>
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              {enabled ? t("syncConfig.disableRegionDesc") : t("syncConfig.enableRegionDesc")}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmDisable(false)}
                className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-600 cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
              >
                {t("syncConfig.cancel")}
              </button>
              <button
                onClick={confirmDisableToggle}
                className="text-sm px-5 py-2 rounded-lg text-white cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
                style={{ backgroundColor: enabled ? "#dc2626" : "#16a34a" }}
              >
                {enabled ? t("syncConfig.disable") : t("syncConfig.enable")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

// ─── Permission Tip ───
function PermissionTip({ t }: { t: (k: string) => string }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex gap-3">
      <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
        <i className="ri-information-line text-amber-500" style={{ fontSize: "16px" }}></i>
      </span>
      <div className="flex flex-col gap-1.5">
        <p className="text-xs font-semibold text-amber-800">{t("syncConfig.permissionTitle")}</p>
        <p className="text-[11px] text-amber-700 leading-relaxed">
          {t("syncConfig.permissionDesc")}
        </p>
      </div>
    </div>
  );
}

// ─── Main Page ───
export default function SyncConfigPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [allUserConfig, setAllUserConfig] = useState<DgSyncConfig | null>(null);
  const [hongKongConfig, setHongKongConfig] = useState<DgSyncConfig | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sourceRef = useRef<SectionHandle>(null);
  const regionRef = useRef<SectionHandle>(null);
  const pollTimerRef = useRef<number | null>(null);
  const pollGenerationRef = useRef(0);
  const savingRef = useRef(false);
  const mountedRef = useRef(true);
  const busy = saving || isDgSyncInProgress(allUserConfig) || isDgSyncInProgress(hongKongConfig);

  // 只展示服务端真实状态；短同步可能在保存后的第一次 GET 前已经完成。
  const applyConfigs = useCallback((result: { allUser: DgSyncConfig; hongKong: DgSyncConfig }) => {
    const pick = (item: DgSyncConfig): DgSyncConfig | null => {
      if (!item.exists) return null;
      return item;
    };
    setAllUserConfig(pick(result.allUser));
    setHongKongConfig(pick(result.hongKong));
  }, []);

  const stopPolling = useCallback(() => {
    pollGenerationRef.current += 1;
    if (pollTimerRef.current !== null) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // 每 3 秒轮询一次配置，直到两个范围都到达终态。
  const startPolling = useCallback(() => {
    if (!mountedRef.current) return;
    stopPolling();
    const generation = pollGenerationRef.current;
    const tick = async () => {
      try {
        const latest = await getPlatformDgSyncConfig();
        if (generation !== pollGenerationRef.current) return;
        applyConfigs(latest);
        const settled = !isDgSyncInProgress(latest.allUser) && !isDgSyncInProgress(latest.hongKong);
        if (settled) {
          pollTimerRef.current = null;
          return;
        }
      } catch {
        // 临时网络失败继续重试，最终状态由服务端超时恢复决定。
      }
      if (generation !== pollGenerationRef.current) return;
      pollTimerRef.current = window.setTimeout(() => void tick(), POLL_INTERVAL_MS);
    };
    pollTimerRef.current = window.setTimeout(() => void tick(), POLL_INTERVAL_MS);
  }, [applyConfigs, stopPolling]);

  // 进入页面时加载两个范围的配置；若仍有同步在进行则恢复轮询。
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getPlatformDgSyncConfig();
        if (cancelled) return;
        applyConfigs(result);
        setIsEditing(!result.allUser.exists || !result.hongKong.exists);
        if (isDgSyncInProgress(result.allUser) || isDgSyncInProgress(result.hongKong)) {
          startPolling();
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof Error && loadError.message
              ? loadError.message
              : t("syncConfig.loadFailed"),
          );
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyConfigs, startPolling, t]);

  // 组件卸载时清理轮询定时器。
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  const handleBack = () => navigate("/admin/users");

  // Save & Sync：两个 section 校验通过后整体 POST，成功后进入同步中并轮询。
  const handleSave = async () => {
    if (savingRef.current || busy) return;
    const allUserPayload = sourceRef.current?.validateAndGet() ?? null;
    const hongKongPayload = regionRef.current?.validateAndGet() ?? null;
    if (!allUserPayload || !hongKongPayload) return;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const { triggered } = await savePlatformDgSyncConfig({
        allUser: allUserPayload,
        hongKong: hongKongPayload,
      });
      const latest = await getPlatformDgSyncConfig();
      applyConfigs(latest);
      setIsEditing(false);
      if (triggered || isDgSyncInProgress(latest.allUser) || isDgSyncInProgress(latest.hongKong)) {
        startPolling();
      }
    } catch (saveError) {
      setError(
        saveError instanceof Error && saveError.message
          ? saveError.message
          : t("syncConfig.saveFailed"),
      );
      // POST 可能已被接受而后续 GET 暂时失败；恢复状态观察以避免展示停留在旧状态。
      startPolling();
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // 已保存状态下切换 Enabled/Disabled：用两个 section 的当前值整体 POST，仅翻转目标侧状态。
  const handleToggleStatus = async (scope: "allUser" | "hongKong", nextEnabled: boolean) => {
    if (!allUserConfig || !hongKongConfig || savingRef.current || busy) return;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const { triggered } = await savePlatformDgSyncConfig({
        allUser: toSaveRequest(
          allUserConfig,
          scope === "allUser" ? nextEnabled : allUserConfig.isEnabled,
        ),
        hongKong: toSaveRequest(
          hongKongConfig,
          scope === "hongKong" ? nextEnabled : hongKongConfig.isEnabled,
        ),
      });
      const latest = await getPlatformDgSyncConfig();
      applyConfigs(latest);
      if (triggered || isDgSyncInProgress(latest.allUser) || isDgSyncInProgress(latest.hongKong)) {
        startPolling();
      }
    } catch (toggleError) {
      setError(
        toggleError instanceof Error && toggleError.message
          ? toggleError.message
          : t("syncConfig.saveFailed"),
      );
      // POST 可能已被接受而后续 GET 暂时失败；恢复状态观察以避免展示停留在旧状态。
      startPolling();
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!allUserConfig && !hongKongConfig) {
      navigate("/admin?tab=users");
      return;
    }
    setIsEditing(false);
  };

  const step1Done = !!(allUserConfig?.exists && allUserConfig.lastSyncCompletedAt);

  return (
    <AdminLayout>
      <div className="px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <button
                onClick={handleBack}
                className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 cursor-pointer whitespace-nowrap transition-colors mb-3"
              >
                <i className="ri-arrow-left-line text-base"></i>
                {t("syncConfig.backToUsers")}
              </button>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold text-gray-900">{t("syncConfig.title")}</h2>
                <QuestionTooltip>
                  <p className="text-xs leading-relaxed">{t("syncConfig.subtitle")}</p>
                </QuestionTooltip>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-xs text-red-600">
            <i className="ri-error-warning-line mt-0.5 shrink-0"></i>
            <span>{error}</span>
          </div>
        )}

        {!loaded ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <i className="ri-loader-4-line text-2xl animate-spin text-gray-300"></i>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            <div className="flex-1 min-w-0 flex flex-col gap-6">
              <UserSourceSection
                ref={sourceRef}
                config={toSectionConfig(allUserConfig)}
                isEditing={isEditing}
                busy={busy}
                onToggleStatus={(v) => void handleToggleStatus("allUser", v)}
              />
              <RegionSection
                ref={regionRef}
                config={toSectionConfig(hongKongConfig)}
                isEditing={isEditing}
                busy={busy}
                step1Done={step1Done}
                onToggleStatus={(v) => void handleToggleStatus("hongKong", v)}
              />

              {/* Unified action bar */}
              {isEditing ? (
                <div className="flex items-center justify-end gap-3 mt-2 border-t border-gray-200 pt-5">
                  <button
                    onClick={handleCancel}
                    disabled={busy}
                    className="text-sm px-5 py-2 rounded-lg border border-gray-200 text-gray-500 cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors disabled:opacity-40"
                  >
                    {t("syncConfig.cancel")}
                  </button>
                  <button
                    onClick={() => void handleSave()}
                    disabled={busy}
                    className="flex items-center gap-1.5 text-sm px-6 py-2 rounded-lg text-white cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90 disabled:opacity-60"
                    style={{ backgroundColor: "#830051" }}
                  >
                    {saving ? (
                      <>
                        <i className="ri-loader-2-line text-sm animate-spin"></i>
                        {t("syncConfig.saving")}
                      </>
                    ) : (
                      <>{t("syncConfig.saveAll")}</>
                    )}
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-end gap-3 mt-2 border-t border-gray-200 pt-5">
                  <button
                    disabled={busy}
                    onClick={() => setIsEditing(true)}
                    className="text-sm px-6 py-2 rounded-lg text-white cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
                    style={{ backgroundColor: "#830051" }}
                  >
                    {t("syncConfig.edit")}
                  </button>
                </div>
              )}
            </div>
            <div className="w-72 shrink-0">
              <PermissionTip t={t} />
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
